import type { FieldPath, FieldValues, UseFormWatch } from "react-hook-form";
import type { FormInputData } from "../../components/BuildingBlocks/Forms/Controllers/FormFieldController";
import type { WizardFormModeProps } from "../../types/wizard.types";
import { camelize } from "../SnakeCamelConversion";

type WizardEditProps<T> = Omit<
  WizardFormModeProps<T> & { mode: "edit" },
  "mode"
>;

type ObjectFormFieldData<TFieldValues extends FieldValues> = {
  [K in FieldPath<TFieldValues>]?: FormInputData<
    TFieldValues,
    K & FieldPath<TFieldValues>
  >;
};

type FieldWithHighlight<TField> = TField extends undefined
  ? TField
  : TField & { highlight: boolean };

/** Form field data after `applyHighlightingToObjectFormFieldData` has set `highlight` on every field. */
type FormFieldDataWithHighlight<TFormFieldData> = {
  [K in keyof TFormFieldData]: FieldWithHighlight<TFormFieldData[K]>;
};

/**
 * Takes form field data and applies `highlight: true/false` to each field
 * based on the wizard's `shouldFieldBeHighlighted` callback. Reads the current
 * value of each field in `fieldsThatCouldBeMissing` via `watch` and passes those
 * values to the highlighting predicate.
 *
 * Form-field keys can be snake_case (e.g. `measurement_description`) while
 * `fieldsThatCouldBeMissing` (and the values map the predicate reads from) are
 * camelCase, because `useSiteWizard` camelizes the missing-field names. Both the
 * collected values and the `fieldName` passed to the predicate are therefore
 * keyed by `camelize(formKey)` so the two namespaces line up. `camelize` is
 * idempotent on already-camelCase keys, so camelCase forms are unaffected.
 *
 * Returns the updated form field data (preserving its original type) and
 * the count of highlighted fields.
 */
export function applyHighlightingToObjectFormFieldData<
  TFieldValues extends FieldValues,
  TFormFieldData extends ObjectFormFieldData<TFieldValues>
>(
  initialFormFieldData: TFormFieldData,
  watch: UseFormWatch<TFieldValues>,
  wizardEditProps?: WizardEditProps<TFieldValues>
) {
  function collectCurrentFormValues() {
    const fieldsThatCouldBeMissing =
      wizardEditProps?.fieldsThatCouldBeMissing ?? [];

    return (Object.keys(initialFormFieldData) as Array<keyof TFormFieldData>)
      .filter((key) =>
        fieldsThatCouldBeMissing.includes(camelize(key as string))
      )
      .reduce<Record<string, unknown>>((values, key) => {
        return {
          ...values,
          [camelize(key as string)]: watch(key as FieldPath<TFieldValues>)
        };
      }, {});
  }

  function getFormFieldDataWithHighlighting(
    currentFormValues: Record<string, unknown> = {}
  ) {
    return (
      Object.keys(initialFormFieldData) as Array<keyof TFormFieldData>
    ).reduce<FormFieldDataWithHighlight<TFormFieldData>>(
      (formFieldData, key) => {
        const field = initialFormFieldData[key];
        const shouldHighlight =
          typeof wizardEditProps?.shouldFieldBeHighlighted === "function"
            ? wizardEditProps.shouldFieldBeHighlighted(
                camelize(key as string),
                currentFormValues
              )
            : false;

        return {
          ...formFieldData,
          [key]: {
            ...field,
            highlight: shouldHighlight
          }
        };
      },
      initialFormFieldData as FormFieldDataWithHighlight<TFormFieldData>
    );
  }

  const currentFormValues = collectCurrentFormValues();
  const formFieldData = getFormFieldDataWithHighlighting(currentFormValues);
  const numHighlightedFields = Object.values(formFieldData).filter(
    (field) =>
      field !== null &&
      typeof field === "object" &&
      "highlight" in field &&
      field.highlight
  ).length;

  return { formFieldData, numHighlightedFields };
}
